import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import JsBarcode from "jsbarcode";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Printer, Tag } from "lucide-react";

const fmtD = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso.length === 10 ? `${iso}T00:00:00` : iso).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "2-digit", year: "numeric" }); } catch { return iso; }
};
const esc = (s) => String(s ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));

const barcodeSvg = (value) => {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  try {
    JsBarcode(svg, String(value || "NA"), { format: "CODE128", width: 1.4, height: 34, displayValue: true, fontSize: 10, margin: 0, textMargin: 1 });
    return svg.outerHTML;
  } catch { return `<div style="font-size:10px">${esc(value)}</div>`; }
};

// Print product labels for a received GRN — 50mm × 30mm (standard small thermal label printers)
export function GrnLabelPrintDialog({ grnId, open, onClose }) {
  const [data, setData] = useState(null);
  const [counts, setCounts] = useState({});
  const [size, setSize] = useState("50x30");
  useEffect(() => {
    if (!open || !grnId) return;
    setData(null);
    api.get(`/grn/${grnId}/labels`).then((r) => {
      setData(r.data);
      const c = {}; (r.data.labels || []).forEach((l, i) => { c[i] = Math.max(1, Math.round(Number(l.quantity || 1))); }); setCounts(c);
    }).catch((e) => toast.error(e?.response?.data?.detail || "Failed to load label data"));
  }, [open, grnId]);
  if (!open) return null;
  const labels = data?.labels || [];
  const total = labels.reduce((s, _, i) => s + (Number(counts[i]) || 0), 0);
  const [w, h] = size === "50x30" ? [50, 30] : size === "58x40" ? [58, 40] : [100, 50];

  const print = () => {
    if (!total) return toast.error("Enter how many labels to print");
    const win = window.open("", "_blank", "width=720,height=800");
    if (!win) return toast.error("Pop-up blocked — allow pop-ups to print labels");
    const cards = [];
    labels.forEach((l, i) => {
      const n = Number(counts[i]) || 0;
      const card = `
        <div class="label">
          <div class="name">${esc(l.name)}</div>
          <div class="row"><span class="price">MRP ₹${Number(l.price || 0).toFixed(2)}</span><span class="dates">MFG ${esc(fmtD(l.mfg_date))} · EXP ${esc(fmtD(l.expiry_date))}</span></div>
          <div class="bc">${barcodeSvg(l.barcode)}</div>
          ${l.ingredients ? `<div class="ing"><b>Ingredients:</b> ${esc(l.ingredients)}</div>` : ""}
          <div class="foot">${esc(data.company_name || "")}${data.fssai_license ? ` · FSSAI ${esc(data.fssai_license)}` : ""}${l.batch_no ? ` · Batch ${esc(l.batch_no)}` : ""}</div>
        </div>`;
      for (let k = 0; k < n; k++) cards.push(card);
    });
    win.document.write(`<html><head><title>Labels ${esc(data.grn_number)}</title><style>
      @page { size: ${w}mm ${h}mm; margin: 0; }
      * { box-sizing: border-box; }
      body { margin: 0; font-family: Arial, Helvetica, sans-serif; color: #000; background: #fff; }
      .label { width: ${w}mm; height: ${h}mm; padding: 1.5mm 2mm; page-break-after: always; break-after: page; overflow: hidden; display: flex; flex-direction: column; }
      .label:last-child { page-break-after: auto; }
      .name { font-size: ${h >= 40 ? 11 : 9.5}px; font-weight: 700; line-height: 1.1; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
      .row { display: flex; justify-content: space-between; align-items: baseline; font-size: ${h >= 40 ? 9 : 7.5}px; margin-top: 0.5mm; }
      .price { font-weight: 700; font-size: ${h >= 40 ? 11 : 9}px; }
      .bc { text-align: center; margin: 0.5mm 0; } .bc svg { max-width: 100%; height: ${h >= 40 ? 11 : 8.5}mm; }
      .ing { font-size: ${h >= 40 ? 7 : 5.8}px; line-height: 1.15; overflow: hidden; display: -webkit-box; -webkit-line-clamp: ${h >= 40 ? 3 : 2}; -webkit-box-orient: vertical; }
      .foot { font-size: ${h >= 40 ? 6.5 : 5.5}px; color: #222; margin-top: auto; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
      @media screen { body { background: #777; padding: 8px; } .label { background: #fff; margin: 0 auto 6px; outline: 1px dashed #999; } .toolbar { position: sticky; top: 0; background: #fff; padding: 8px; margin: -8px -8px 8px; display: flex; gap: 8px; align-items: center; font-size: 13px; } }
      @media print { .toolbar { display: none; } }
    </style></head><body>
      <div class="toolbar"><button onclick="window.print()" style="background:#C05621;color:#fff;border:0;padding:8px 14px;border-radius:6px;cursor:pointer">Print ${total} label${total === 1 ? "" : "s"}</button><span>${w}×${h} mm · set printer paper size to match and disable scaling</span></div>
      ${cards.join("")}
    </body></html>`);
    win.document.close();
    setTimeout(() => { win.focus(); win.print(); }, 500);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="grn-label-dialog">
        <DialogHeader>
          <DialogTitle className="font-[Outfit] flex items-center gap-2"><Tag className="w-5 h-5 text-[#C05621]" /> Print product labels · {data?.grn_number || "…"}</DialogTitle>
          <DialogDescription>Each label: barcode, product name, MRP, MFG & EXP dates, ingredients. Choose how many labels per product.</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2 text-sm">
          <span className="text-[#8B7355]">Label size</span>
          {[["50x30", "50 × 30 mm"], ["58x40", "58 × 40 mm"], ["100x50", "100 × 50 mm"]].map(([k, l]) => (
            <Button key={k} size="sm" variant={size === k ? "default" : "outline"} onClick={() => setSize(k)} data-testid={`grn-label-size-${k}`} className={size === k ? "bg-[#C05621] hover:bg-[#9C4221] text-white" : ""}>{l}</Button>
          ))}
        </div>
        {!data ? <div className="py-6 text-center text-[#8B7355]">Loading…</div> : labels.length === 0 ? (
          <div className="py-6 text-center text-[#8B7355]" data-testid="grn-label-empty">No received items on this GRN.</div>
        ) : (
          <div className="border border-[#EAE5D9] rounded-md overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left uc-label border-b border-[#EAE5D9] bg-[#FDFBF7]">
                <th className="px-3 py-2">Product</th><th className="px-3 py-2">Barcode</th><th className="px-3 py-2 text-right">MRP</th><th className="px-3 py-2">MFG / EXP</th><th className="px-3 py-2 text-right">Received</th><th className="px-3 py-2 text-right">Labels</th>
              </tr></thead>
              <tbody>
                {labels.map((l, i) => (
                  <tr key={i} className="border-b border-[#EAE5D9] last:border-0" data-testid={`grn-label-row-${i}`}>
                    <td className="px-3 py-2"><div className="font-medium">{l.name}</div>{l.ingredients ? <div className="text-[11px] text-[#8B7355] line-clamp-1">Ingredients: {l.ingredients}</div> : <div className="text-[11px] text-amber-700">No ingredients on product master</div>}</td>
                    <td className="px-3 py-2 font-mono text-xs">{l.barcode || "—"}{l.batch_no ? <div className="text-[10px] text-[#8B7355]">Batch {l.batch_no}</div> : null}</td>
                    <td className="px-3 py-2 text-right">₹{Number(l.price || 0).toFixed(2)}</td>
                    <td className="px-3 py-2 text-xs">{fmtD(l.mfg_date)}<br />{fmtD(l.expiry_date)}</td>
                    <td className="px-3 py-2 text-right">{l.quantity} {l.unit}</td>
                    <td className="px-3 py-2 text-right">
                      <Input type="number" min="0" value={counts[i] ?? ""} onChange={(e) => setCounts((c) => ({ ...c, [i]: e.target.value }))} className="h-8 w-20 text-right ml-auto" data-testid={`grn-label-count-${i}`} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="grn-label-cancel">Close</Button>
          <Button onClick={print} disabled={!data || !total} data-testid="grn-label-print-btn" className="bg-[#C05621] hover:bg-[#9C4221] text-white"><Printer className="w-4 h-4 mr-1" /> Print {total} label{total === 1 ? "" : "s"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
